import { useEffect, useRef, useState } from 'react';
import { X, Loader2, Expand } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getAuthToken, getPortalType } from '@/utils/auth';
import { toast } from 'sonner';

/**
 * Inline (non-modal) PDF preview pane that fetches the PDF via authenticated proxy URL
 * and renders it in an iframe. Intended for split-pane layouts (YouLearn-like).
 */
export default function InlinePDFPreview({
  pdfUrl,
  title,
  page = 1,
  bbox = null, // [x1, y1, x2, y2] for text highlighting
  onClose,
  onOpenFullScreen,
  onTextSelection,
}) {
  const [iframeBaseUrl, setIframeBaseUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filename, setFilename] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const iframeRef = useRef(null);
  const selectionCleanupRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const preparePdf = async () => {
      if (!pdfUrl) {
        setFilename(null);
        setIframeBaseUrl(null);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        setFilename(null); // Reset filename when fetching new PDF
        const portal = getPortalType(window.location.pathname || '');
        const token = getAuthToken(portal);
        if (!token) throw new Error('Authentication required');

        // Extract filename from URL (best-effort)
        let extractedFilename = null;
        if (pdfUrl) {
          try {
            const urlPath = new URL(pdfUrl).pathname;
            const pathParts = urlPath.split('/').filter(Boolean);
            const pdfPart = pathParts.find((part) => part.toLowerCase().endsWith('.pdf'));
            extractedFilename = pdfPart || pathParts[pathParts.length - 1] || null;
          } catch {
            extractedFilename = null;
          }
        }

        // Bypassing backend cache proxy to resolve 504 Gateway Timeout issues.
        // The original pdfUrl is used directly.
        const effectiveUrl = pdfUrl;

        if (!alive) return;
        if (extractedFilename) setFilename(extractedFilename);
        setIframeBaseUrl(effectiveUrl);
      } catch (err) {
        console.error('Error fetching PDF:', err);
        if (!alive) return;
        setError('Failed to load PDF. Please try again.');
        toast.error('Failed to load PDF');
        setIframeBaseUrl(null);
      } finally {
        if (alive) setLoading(false);
      }
    };

    preparePdf();
    return () => {
      alive = false;
    };
  }, [pdfUrl, reloadKey]);

  useEffect(() => {
    return () => {
      if (typeof selectionCleanupRef.current === 'function') {
        selectionCleanupRef.current();
      }
      selectionCleanupRef.current = null;
    };
  }, []);

  return (
    <div className="h-full w-full flex flex-col bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-xl overflow-hidden">
      <div className="px-3 py-2 flex items-center justify-between border-b border-[hsl(var(--card-border))] bg-[hsl(var(--card-bg))]">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">
            {filename || title || 'Textbook PDF'}
          </p>
          <p className="text-xs text-muted-foreground">Page {page}</p>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={onOpenFullScreen}
            className="text-muted-foreground hover:text-foreground"
            title="Open full screen"
          >
            <Expand className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            title="Close preview"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="relative flex-1 bg-gray-900 min-h-0 overflow-hidden">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-[hsl(var(--card-bg))]">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-7 h-7 animate-spin text-[hsl(var(--primary))]" />
              <p className="text-muted-foreground text-sm">Loading PDF…</p>
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-[hsl(var(--card-bg))]">
            <div className="text-center px-4">
              <p className="text-red-400 mb-3">{error}</p>
              <Button
                onClick={() => {
                  setError(null);
                  setLoading(true);
                  setReloadKey((k) => k + 1);
                }}
              >
                Retry
              </Button>
            </div>
          </div>
        )}

        {iframeBaseUrl && !loading && !error && (
          <div className="relative w-full h-full overflow-hidden">
            <div className="absolute inset-0 w-full h-full">
              <iframe
                // Hide sidebar/nav pane by default (thumbnails/outlines)
                // Use page parameter to navigate to specific page
                // If bbox is provided, add viewrect for highlighting (PDF.js format)
                src={(() => {
                  const pageNum = Math.max(1, parseInt(page) || 1);
                  // Add cache-buster query param when reloading to avoid stale iframe content.
                  let url = `${iframeBaseUrl}&_=${reloadKey}#page=${pageNum}&view=FitH&navpanes=0&pagemode=none&zoom=page-width&scrollbar=1`;

                  // Add bbox coordinates for highlighting if available
                  // PDF.js viewrect format: x1,y1,x2,y2 (in points, 1/72 inch)
                  // Our bbox is typically in pixels, but PDF.js will handle conversion
                  if (bbox && Array.isArray(bbox) && bbox.length === 4) {
                    const [x1, y1, x2, y2] = bbox;
                    // Convert to PDF coordinate system if needed (PDF.js handles this, but we can normalize)
                    // For now, pass directly - PDF.js will interpret it
                    url += `&viewrect=${x1},${y1},${x2 - x1},${y2 - y1}`;
                  }

                  return url;
                })()}
                className="w-full h-full border-0"
                style={{ minHeight: '100%' }}
                title="PDF Preview"
                onLoad={() => {
                  setLoading(false);
                  // Best-effort selection capture (may not work in all browsers' native PDF viewers)
                  if (typeof selectionCleanupRef.current === 'function') {
                    selectionCleanupRef.current();
                    selectionCleanupRef.current = null;
                  }

                  if (typeof onTextSelection === 'function') {
                    const iframeEl = iframeRef.current;
                    try {
                      const win = iframeEl?.contentWindow;
                      const doc = win?.document;
                      if (win && doc) {
                        const emit = () => {
                          try {
                            const sel = win.getSelection?.();
                            const text = sel?.toString?.() || '';
                            if (!text.trim() || !sel || sel.rangeCount === 0) {
                              onTextSelection(null);
                              return;
                            }
                            const range = sel.getRangeAt(0);
                            const r = range.getBoundingClientRect();
                            const iframeRect = iframeEl.getBoundingClientRect();
                            onTextSelection({
                              text,
                              rect: {
                                top: iframeRect.top + r.top,
                                left: iframeRect.left + r.left,
                                right: iframeRect.left + r.right,
                                bottom: iframeRect.top + r.bottom,
                                width: r.width,
                                height: r.height,
                              },
                            });
                          } catch {
                            onTextSelection(null);
                          }
                        };

                        // Mouse selection + keyboard selection
                        doc.addEventListener('mouseup', emit);
                        doc.addEventListener('keyup', emit);
                        doc.addEventListener('selectionchange', emit);

                        selectionCleanupRef.current = () => {
                          doc.removeEventListener('mouseup', emit);
                          doc.removeEventListener('keyup', emit);
                          doc.removeEventListener('selectionchange', emit);
                        };
                      }
                    } catch {
                      // Cross-origin or blocked access: do nothing
                      onTextSelection(null);
                    }
                  }

                  // Instant navigation - no delay
                  // Try to highlight the bbox region if provided
                  if (bbox && Array.isArray(bbox) && bbox.length === 4) {
                    try {
                      const iframe = iframeRef.current;
                      if (iframe && iframe.contentWindow) {
                        // Some PDF viewers support programmatic highlighting
                        // This is a fallback if URL fragment doesn't work
                        requestAnimationFrame(() => {
                          try {
                            if (iframe.contentWindow) {
                              // Try postMessage for PDF.js highlighting
                              iframe.contentWindow.postMessage({
                                type: 'highlight',
                                bbox: bbox
                              }, '*');
                            }
                          } catch (e) {
                            console.debug('Could not send highlight message:', e);
                          }
                        });
                      }
                    } catch (e) {
                      // Cross-origin restrictions might prevent this
                      console.debug('Could not access iframe content for highlighting:', e);
                    }
                  }
                }}
                ref={iframeRef}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


